import { invalidate } from '@react-three/fiber';
import { useEffect, useMemo } from 'react';
import { DoubleSide, MeshBasicMaterial } from 'three';
import { useShallow } from 'zustand/shallow';

import { GetMainRepresentationByArticleIdLayerLoadMode as LayerLoadMode, type PrefabModel } from 'api/models';
import { useGetMainRepresentationByArticleId } from 'api/queries/articles/articles';
import {
    buildPrefabOverlayItems,
    type OverlayItem
} from 'app/modules/wallforming/components/scenes/prefab-layer-renderer/utils/prefabGeometryUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

// ─── Shared material cache ────────────────────────────────────────────────────

const meshMaterialCache = new Map<string, MeshBasicMaterial>();

function getMeshMaterial(color: string, opacity: number, doubleSide: boolean): MeshBasicMaterial {
    const key = `${color}_${opacity}_${doubleSide}`;
    if (!meshMaterialCache.has(key)) {
        meshMaterialCache.set(
            key,
            new MeshBasicMaterial({
                color,
                transparent: opacity < 1,
                opacity,
                side: doubleSide ? DoubleSide : undefined,
                depthTest: false
            })
        );
    }

    return meshMaterialCache.get(key)!;
}

// ─── Single item ─────────────────────────────────────────────────────────────

const OverlayMeshItem = ({ item }: { item: OverlayItem }) => {
    if (item.renderMode === 'mesh') {
        return (
            <mesh
                geometry={item.geometry}
                material={getMeshMaterial(item.color, item.opacity, item.doubleSide)}
                renderOrder={99}
            />
        );
    }

    if (item.renderMode === 'line') {
        return (
            // @ts-expect-error – lowercase 'line' is the r3f Three.js Line primitive
            <line geometry={item.geometry} renderOrder={99}>
                <lineBasicMaterial attach="material" color={item.color} depthTest={false} />
            </line>
        );
    }

    return null;
};

// ─── Public component ─────────────────────────────────────────────────────────

interface PrefabLayerPreviewRendererProps {
    /** Selected article ID used to fetch all layer geometry for the prefab tree overlay. */
    articleId: string;
}

/**
 * Development-only r3f overlay rendered inside the article preview canvas.
 * The article preview renders at world origin so no instance transform is needed.
 * Only active when prefabLayerTreeOpen is true.
 */
export const PrefabLayerPreviewRenderer = ({ articleId }: PrefabLayerPreviewRendererProps) => {
    const { prefabLayerTreeOpen, layerVisibility, partLayerVisibility } = usePlacementStore(
        useShallow((state) => ({
            prefabLayerTreeOpen: state.prefabLayerTreeOpen,
            layerVisibility: state.layerVisibility,
            partLayerVisibility: state.partLayerVisibility
        }))
    );

    const { data } = useGetMainRepresentationByArticleId(
        articleId,
        { layerLoadMode: LayerLoadMode.All, includePrefabComponents: false },
        {
            query: {
                queryKey: ['representation', articleId, LayerLoadMode.All],
                staleTime: 24 * 60 * 60 * 1000,
                enabled: prefabLayerTreeOpen
            }
        }
    );

    const articleRepresentation: PrefabModel | undefined = data?.data;

    const overlayItems = useMemo(() => {
        if (!prefabLayerTreeOpen || !articleRepresentation) {
            return [];
        }
        return buildPrefabOverlayItems(articleRepresentation, layerVisibility, partLayerVisibility);
    }, [prefabLayerTreeOpen, articleRepresentation, layerVisibility, partLayerVisibility]);

    // Preview canvas is frameloop="demand" — invalidate after item changes
    useEffect(() => {
        invalidate();
    }, [overlayItems]);

    if (!prefabLayerTreeOpen || overlayItems.length === 0) {
        return null;
    }

    return (
        <group renderOrder={99}>
            {overlayItems.map((item) => (
                <OverlayMeshItem key={item.id} item={item} />
            ))}
        </group>
    );
};
